import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Divider, message, Row, Spin } from 'antd';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { metricKeys } from '@pulse/metrics';
import {
  aiAlerts1minPersistenceSelector,
  aiAlertsPCPFeatureSelector,
  isAdminRoleSelector,
} from '@src/store/common/selectors';
import { getMetricDisplayName } from '@src/utils/metrics';
import { PRODUCT_MODE_MAP } from '@src/utils/constants';
import { DEFAULT_SENSITIVITY_CONFIG_FOR_METRIC } from '@src/utils/aiAlerts/mapping';
import {
  useFetchLegacySensitivity,
  useUpdateLegacySensitivity,
} from '@src/components/ai-alerts/hooks/useSensitivity';
import { selectedProductAtom } from './atoms';
import {
  StyledConfigLabel,
  StyledInput,
  StyledInputWrapper,
  StyledUnit,
} from './SensitivityForms/styled';
import { FooterWrapper } from './styled';
import { checkErrors } from './errorUtils';
import { SensitivityConfig } from './interface';

/**
 * Concurrent Plays sensitivity is intentionally excluded from the CCS migration
 * (SENSITIVITY_MIGRATION_EXCLUDED_METRICS in the server) and continues to live
 * exclusively in the portal MySQL `customer_portal_sensitivity` table. This
 * standalone card fetches/saves it via the v2 legacy-metrics endpoints so the
 * main Sensitivity form's CCS write path is never invoked with a PCP row.
 *
 * Self-contained state (does NOT use fullSensitivityConfigAtom) so PCP edits
 * are isolated from the main form's diffing logic.
 */
const ConcurrentPlaysSection = ({ isPopupOpen }: { isPopupOpen: boolean }) => {
  const isPcpEnabled = useSelector(aiAlertsPCPFeatureSelector);
  const isAdminUser = useSelector(isAdminRoleSelector);
  const is1minEnabled = useSelector(aiAlerts1minPersistenceSelector);
  // Backend wait-to-start is 2 min, so the real floor is 2, not 1 (CE-12272).
  const minSustain = is1minEnabled ? 2 : 3;
  const selectedProduct = useAtomValue(selectedProductAtom);

  const enabled = isPopupOpen && !!isPcpEnabled && selectedProduct === PRODUCT_MODE_MAP.EI;

  const { data: legacyRows, isFetching, refetch } = useFetchLegacySensitivity(enabled);
  const updateLegacySensitivity = useUpdateLegacySensitivity();

  const savedConfig: SensitivityConfig | null = useMemo(() => {
    const row = (legacyRows || []).find((r: any) => r?.metricKey === metricKeys.ConcurrentPlays);
    return row || null;
  }, [legacyRows]);

  const [draft, setDraft] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (savedConfig) {
      setDraft({ ...savedConfig });
    } else {
      setDraft({
        ...DEFAULT_SENSITIVITY_CONFIG_FOR_METRIC,
        metricKey: metricKeys.ConcurrentPlays,
      });
    }
  }, [enabled, savedConfig]);

  if (!enabled || !draft) return null;

  const onChange = (key: string) => (value: any) => {
    setDraft((prev: any) => ({ ...prev, [key]: value }));
  };

  const errorMsg = checkErrors(draft);

  const onSave = () => {
    setIsSaving(true);
    // The v2 legacy endpoint expects an array; only PCP is allowed.
    updateLegacySensitivity.mutate([draft], {
      onSuccess: () => {
        setIsSaving(false);
        message.success('Concurrent Plays sensitivity updated successfully');
        refetch();
      },
      onError: () => {
        setIsSaving(false);
        message.error('There was an error while saving Concurrent Plays. Please try again later.');
      },
    });
  };

  const metricName = getMetricDisplayName({
    metricKey: metricKeys.ConcurrentPlays,
    context: selectedProduct,
  });

  return (
    <div
      style={{
        marginTop: '24px',
        padding: '12px 16px',
        border: '1px solid #f0f0f0',
        borderRadius: '6px',
      }}
    >
      <Spin spinning={isFetching}>
        <Divider orientation="left" orientationMargin="0" style={{ margin: '10px 0' }}>
          {metricName}
        </Divider>

        <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 16]}>
          <Col span={9}>
            <StyledConfigLabel>Persistence exceeds</StyledConfigLabel>
          </Col>
          <Col span={5}>
            <StyledInputWrapper>
              <StyledInput
                disabled={!isAdminUser}
                min={minSustain}
                max={60}
                onChange={onChange('sustain')}
                value={draft.sustain}
              />
              <StyledUnit>min</StyledUnit>
            </StyledInputWrapper>
          </Col>
          <Col span={5}>
            <StyledInputWrapper>
              <StyledInput
                disabled={!isAdminUser}
                min={minSustain}
                max={60}
                onChange={onChange('warningSustain')}
                value={draft.warningSustain}
              />
              <StyledUnit>min</StyledUnit>
            </StyledInputWrapper>
          </Col>
          <Col span={5}>
            <StyledInputWrapper>
              <StyledInput
                disabled={!isAdminUser}
                min={minSustain}
                max={60}
                onChange={onChange('criticalSustain')}
                value={draft.criticalSustain}
              />
              <StyledUnit>min</StyledUnit>
            </StyledInputWrapper>
          </Col>
        </Row>

        <div style={{ marginTop: '12px' }}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={9}>
              <StyledConfigLabel>Concurrency declines</StyledConfigLabel>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={100}
                  onChange={onChange('sustainPcpDrop')}
                  value={draft.sustainPcpDrop}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={100}
                  onChange={onChange('warningPcpDrop')}
                  value={draft.warningPcpDrop}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
            <Col span={5}>
              <StyledInputWrapper>
                <StyledInput
                  disabled={!isAdminUser}
                  min={0}
                  max={100}
                  onChange={onChange('criticalPcpDrop')}
                  value={draft.criticalPcpDrop}
                />
                <StyledUnit>%</StyledUnit>
              </StyledInputWrapper>
            </Col>
          </Row>
        </div>

        {errorMsg ? <Alert message={errorMsg} type="error" style={{ marginTop: '12px' }} /> : null}

        <FooterWrapper>
          <Button
            type="primary"
            onClick={onSave}
            loading={isSaving}
            disabled={!isAdminUser || !!errorMsg || isSaving}
          >
            Save Concurrent Plays
          </Button>
        </FooterWrapper>
      </Spin>
    </div>
  );
};

export default React.memo(ConcurrentPlaysSection);
